import React, { useCallback, useState } from 'react';

import { ContextResponse } from 'yeself-common/typings/context/Context';
import { LayerId, LayerNodeId } from 'yeself-common/typings/guide/LayerCommon';
import { RULE_TYPE } from 'yeself-common/enum/RuleEnum';

import AdoptContextRuleField from 'yeself-client-common/form/rules/components/AdoptContextRuleField';
import SelectFieldGrouped from 'yeself-client-common/form/components/SelectFieldGrouped';

import { RuleValues } from '../types';

import CountRuleField from './CountRuleField';
import FacesGuideRuleField from './FacesGuideRuleField';
import LastSeenGuideRuleField from './LastSeenGuideRuleField';
import RelatedGuideRuleField from './RelatedGuideRuleField';
import RolesRuleField from './RolesRuleField';
import SegmentationValueRuleField from './SegmentationValueRuleField';
import ShouldSeeGuideRuleField from './ShouldSeeGuideRuleField';
import UserAttributeRuleField from './UserAttributeValueRuleField';
import UserIdsRuleField from './UserIdsRuleField';
import TimeModuloRuleField from './TimeModuloRuleField';
import VisitedCountRuleField from './VisitedCountRuleField';
import InteractedWithRuleField from './InteractedWithRuleField';

type RuleFieldProps = {
    value: RuleValues;
    onChange: (value: RuleValues) => void;
    options: {
        guideNames: { value: LayerId; label: string }[];
        attributeNames: { value: string; label: string }[];
        interactionCollectorNodeNames: {
            value: LayerId;
            label: string;
            options: { value: LayerNodeId; label: string }[];
        }[];
        contextResponse: ContextResponse;
    };
};

const RuleField = ({ value, onChange, options }: RuleFieldProps): React.ReactElement | null => {
    switch (value.type) {
        case RULE_TYPE.SEEN_COUNT:
        case RULE_TYPE.FINISHED_COUNT:
        case RULE_TYPE.COMPLETED_COUNT:
            return <CountRuleField value={value} onChange={onChange} />;
        case RULE_TYPE.FACES_GUIDE:
            return <FacesGuideRuleField value={value} onChange={onChange} />;
        case RULE_TYPE.SHOULD_SEE_GUIDE:
            return <ShouldSeeGuideRuleField value={value} onChange={onChange} />;
        case RULE_TYPE.LAST_SEEN_GUIDE:
            return <LastSeenGuideRuleField value={value} onChange={onChange} />;
        case RULE_TYPE.SEGMENTATION_VALUE:
            return <SegmentationValueRuleField value={value} onChange={onChange} />;
        case RULE_TYPE.USER_IDS:
            return <UserIdsRuleField value={value} onChange={onChange} />;
        case RULE_TYPE.ROLES:
            return <RolesRuleField value={value} onChange={onChange} />;
        case RULE_TYPE.ADOPT_ROLES:
            return (
                <AdoptContextRuleField
                    value={value}
                    onChange={onChange}
                    options={options.contextResponse.roles}
                />
            );
        case RULE_TYPE.ADOPT_LANGUAGES:
            return (
                <AdoptContextRuleField
                    value={value}
                    onChange={onChange}
                    options={options.contextResponse.languages}
                />
            );
        case RULE_TYPE.ADOPT_LOCATIONS:
            return (
                <AdoptContextRuleField
                    value={value}
                    onChange={onChange}
                    options={options.contextResponse.locations}
                />
            );
        case RULE_TYPE.USER_ATTRIBUTE_VALUE:
            return (
                <UserAttributeRuleField
                    value={value}
                    onChange={onChange}
                    options={options.attributeNames}
                />
            );
        case RULE_TYPE.FINISHED_RELATED_GUIDE:
        case RULE_TYPE.COMPLETED_RELATED_GUIDE:
        case RULE_TYPE.SEEN_RELATED_GUIDE:
        case RULE_TYPE.HAS_NOT_SEEN_RELATED_GUIDE:
            return (
                <RelatedGuideRuleField value={value} onChange={onChange} options={options.guideNames} />
            );
        case RULE_TYPE.TIME_MODULO:
            return <TimeModuloRuleField value={value} onChange={onChange} />;
        case RULE_TYPE.VISITED_COUNT:
            return <VisitedCountRuleField value={value} onChange={onChange} />;
        case RULE_TYPE.INTERACTED_WITH:
            return (
                <InteractedWithRuleField
                    options={options.interactionCollectorNodeNames}
                    value={value}
                    onChange={onChange}
                />
            );
        case RULE_TYPE.HAS_NOT_COMPLETED:
        case RULE_TYPE.HAS_NOT_FINISHED:
        case RULE_TYPE.HAS_NOT_SEEN:
        case RULE_TYPE.DO_NOT_START_AUTOMATICALLY:
        case RULE_TYPE.SIGNED_IN:
        default:
            return null;
    }
};

type RuleItemProps = {
    value: RuleValues;
    onChange: (value: RuleValues) => void;
    options: {
        contextResponse: ContextResponse;
        ruleType: {
            label: string | React.ReactNode,
            options: { value: typeof RULE_TYPE[keyof typeof RULE_TYPE]; label: string | React.ReactNode }[]
        }[];
        guideNames: { value: LayerId; label: string }[];
        attributeNames: { value: string; label: string }[];
        interactionCollectorNodeNames: {
            value: LayerId;
            label: string;
            options: { value: LayerNodeId; label: string }[];
        }[];
    };
};

const RuleItem = ({ value, onChange = () => undefined, options }: RuleItemProps): React.ReactElement => {
    const [ruleType, setRuleType] = useState(value.type);

    const handleOnTypeChange = useCallback(
        (newValue) => {
            setRuleType(newValue);
            onChange({ ...value, type: newValue, level: [] });
        },
        [onChange, value]
    );

    const handleOnChange = useCallback(
        (newValue) => {
            onChange(newValue);
        },
        [onChange]
    );

    return (
        <>
            <SelectFieldGrouped
                value={ruleType}
                onChange={handleOnTypeChange}
                options={options.ruleType}
            />
            {ruleType ? (
                <RuleField
                    value={value}
                    onChange={handleOnChange}
                    options={{
                        guideNames: options.guideNames,
                        interactionCollectorNodeNames: options.interactionCollectorNodeNames,
                        attributeNames: options.attributeNames,
                        contextResponse: options.contextResponse,
                    }}
                />
            ) : null}
        </>
    );
};

export default RuleItem;
